import { Mic, MicOff, Video, VideoOff, MonitorUp, MonitorOff, Hand, PhoneOff, MoreHorizontal, MessageSquare, Users, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useWebinarStore from '@/stores/webinarStore';

export default function MediaControls({ onLeave, onToggleScreen, isScreenSharing, onEndWebinar, isHost }) {
  const { isMuted, isCameraOn, toggleMute, toggleCamera, toggleHandRaise, raisedHands, sendReaction } = useWebinarStore();
  const currentUserId = useWebinarStore(s => s.webinar?.created_by); // rough check

  const handRaised = raisedHands.includes(currentUserId);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex items-center justify-center gap-2 px-4 py-3 bg-zinc-900/95 backdrop-blur border-t border-zinc-800">
        {/* Mic */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost" size="icon"
              onClick={toggleMute}
              className={`h-11 w-11 rounded-full transition-all ${
                isMuted ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isMuted ? 'Activer le micro' : 'Couper le micro'}</TooltipContent>
        </Tooltip>

        {/* Camera */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost" size="icon"
              onClick={toggleCamera}
              className={`h-11 w-11 rounded-full transition-all ${
                !isCameraOn ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isCameraOn ? 'Couper la caméra' : 'Activer la caméra'}</TooltipContent>
        </Tooltip>

        {/* Screen Share */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost" size="icon"
              onClick={onToggleScreen}
              className={`h-11 w-11 rounded-full transition-all ${
                isScreenSharing ? 'bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 ring-2 ring-cyan-500/30' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              {isScreenSharing ? <MonitorOff className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{isScreenSharing ? 'Arrêter le partage' : 'Partager l\'écran'}</TooltipContent>
        </Tooltip>

        {/* Hand Raise */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost" size="icon"
              onClick={toggleHandRaise}
              className={`h-11 w-11 rounded-full transition-all ${
                handRaised ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30' : 'bg-zinc-800 text-white hover:bg-zinc-700'
              }`}
            >
              <Hand className="w-5 h-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{handRaised ? 'Baisser la main' : 'Lever la main'}</TooltipContent>
        </Tooltip>

        {/* Reactions */}
        <div className="flex items-center gap-0.5 mx-2">
          {['👍', '❤️', '😂', '👏', '🔥'].map(emoji => (
            <button
              key={emoji}
              onClick={() => sendReaction(emoji)}
              className="w-9 h-9 rounded-full hover:bg-zinc-800 flex items-center justify-center text-base transition-transform hover:scale-125 active:scale-90"
            >
              {emoji}
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-zinc-800 mx-2" />

        {/* Leave / End */}
        {isHost ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={onEndWebinar}
                className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-700 text-white font-medium gap-2">
                <PhoneOff className="w-4 h-4" /> Terminer
              </Button>
            </TooltipTrigger>
            <TooltipContent>Terminer le webinaire pour tous</TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button onClick={onLeave}
                className="h-11 px-5 rounded-full bg-red-600 hover:bg-red-700 text-white font-medium gap-2">
                <PhoneOff className="w-4 h-4" /> Quitter
              </Button>
            </TooltipTrigger>
            <TooltipContent>Quitter le webinaire</TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
